/* eslint-disable import/no-cycle */
import Vue from 'vue';
import VueRouter from 'vue-router';
import StorageService from '@/services/storage.service';
import ApiService from '@/services/api.service';
import UserService from '@/services/user.service';
import * as routerHelper from '@/helpers/router.helper';
import { bookingEngineId } from '@/shared';
import mappingMasterRoutes from '@/router/mappingMasterRoutes';
import eHotelSuiteRoutes from '@/router/eHotelSuiteRoutes';
import authRoutes from '@/router/authRoutes';
import testAuthRoutes from '@/router/testAuthRoutes';
import hotelLinkageRoutes from '@/router/hotelLinkage';

Vue.use(VueRouter);

const routes = [
  {
    path: '/',
    name: 'home',
    meta: {
      translationKey: ['home'],
    },
    component: () => import(/* webpackChunkName: "home" */ '@/views/Layout/AppLayout.vue'),
    children: [
      {
        path: '/setup',
        name: routerHelper.setupRouteName,
        meta: {
          title: 'Setup Wizard',
          stretch: true,
          translationKey: ['setupWizard'],
        },
        component: () => import(/* webpackChunkName: "user-setup" */ '@/views/User/Setup.vue'),
      },
      {
        path: '/details',
        name: routerHelper.detailsRouteName,
        meta: {
          centered: true,
          translationKey: ['details'],
          shared: true,
        },
        component: () => import(/* webpackChunkName: "user-details" */ '@/views/User/Details.vue'),
      },
      {
        path: '/email/verify',
        name: routerHelper.verificationNoticeRouteName,
        meta: {
          title: 'Verification',
          documentTitle: '*signup.verify.title',
          centered: true,
          agent: false,
          shared: true,
          translationKey: ['verificationNotice'],
          public: true,
        },
        component: () => import(/* webpackChunkName: "verification-notice" */ '@/views/User/VerifyEmailNotice.vue'),
      },
      {
        path: '/maintenance',
        name: 'maintenance',
        meta: {
          title: 'Maintenance',
          centered: true,
          public: true,
          maintenance: true,
          translationKey: ['maintenance'],
        },
        component: () => import(/* webpackChunkName: "maintenance" */ '@/views/StaticPages/Maintenance.vue'),
      },
      {
        path: '/calendar/sellable-units',
        name: 'calendarSellableUnit',
        meta: {
          title: 'Calendar',
          documentTitle: '*calendar.pageTitleSellableUnits',
          translationKey: ['calendarSellableUnit'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "calendar" */ '@/views/Calendar/SellableUnitsCalendar.vue'),
      },
      {
        path: '/calendar/products',
        name: 'calendarProducts',
        meta: {
          title: 'Calendar',
          documentTitle: '*calendar.pageTitleProducts',
          translationKey: ['calendarProducts'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "calendar" */ '@/views/Calendar/ProductsCalendar.vue'),
      },
      {
        path: '/calendar/products-plus',
        name: 'productPlusCalendar',
        meta: {
          title: 'Calendar',
          documentTitle: '*calendar.pageTitleProductsPlus',
          translationKey: ['productPlusCalendar'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "calendarPlus" */ '@/views/Calendar/ProductPlusCalendar.vue'),
      },
      {
        path: '/billing',
        name: 'billing',
        meta: {
          title: 'BillingAddress',
          documentTitle: '*pages.billingAddress.title',
          agent: false,
          translationKey: ['billing'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "billingAddress" */ '@/views/User/Billing.vue'),
      },
      {
        path: '/profile',
        name: 'profile',
        meta: {
          title: 'Profile Settings',
          documentTitle: '*pages.profile.title',
          empty: true,
          agent: false,
          translationKey: ['profile'],
        },
        component: () => import(/* webpackChunkName: "profile" */ '@/views/User/Profile.vue'),
      },
      {
        path: '/follower',
        name: 'follower',
        meta: {
          title: 'Follower',
          documentTitle: '*pages.follow.title',
          empty: true,
          agent: false,
          translationKey: ['follower'],
        },
        component: () => import(/* webpackChunkName: "profile" */ '@/views/User/Follower.vue'),
      },
      {
        path: '/user/properties',
        name: 'userProperties',
        meta: {
          title: 'Properties',
          documentTitle: '*pages.properties.title',
          empty: true,
          agent: false,
          translationKey: ['userProperties'],
        },
        component: () => import(/* webpackChunkName: "profile" */ '@/views/User/Properties.vue'),
      },
      {
        path: '/dashboard',
        name: routerHelper.dashboardRouteName,
        meta: {
          title: 'Dashboard',
          documentTitle: '*dashboard.pageTitle',
          translationKey: ['dashboard', 'navbarTitles', 'menu'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "dashboard" */ '@/views/Dashboard/Dashboard.vue'),
      },
      {
        path: '/reservations',
        name: 'reservations',
        meta: {
          title: 'Reservations',
          documentTitle: '*reservations.pageTitle',
          translationKey: ['reservations'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "reservations" */ '@/views/Reservations/Reservations.vue'),
      },
      {
        path: '/legal',
        name: 'legal',
        meta: {
          agent: false,
          translationKey: ['legal'],
        },
        component: () => import(/* webpackChunkName: "legal" */ '@/views/User/Legal.vue'),
      },
      {
        path: '/users/:tab?',
        name: 'users',
        meta: {
          title: 'Users & Roles',
          documentTitle: '*pages.users.title',
          empty: true,
          translationKey: ['users'],
          loadMeasurement: true,
          pageID: 228,
        },
        component: () => import(/* webpackChunkName: "users" */ '@/views/Management/UserRolePanel.vue'),
      },
      {
        path: '/messages/:tab?',
        name: 'messages',
        meta: {
          title: 'Messages',
          documentTitle: '*pages.messages.title',
          translationKey: ['messages'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "messages" */ '@/views/Management/Messages.vue'),
      },
      {
        path: '/my-messages',
        name: 'propertyMessages',
        meta: {
          title: 'MyMessages',
          documentTitle: '*pages.messages.title',
          translationKey: ['messages'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "messages" */ '@/views/Management/MyMessages.vue'),
      },
      {
        path: '/meal-plans',
        name: 'mealplans',
        meta: {
          title: 'Meal plans',
          documentTitle: '*mealPlans.pageTitle',
          translationKey: ['mealPlans'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "meal-plans" */ '@/views/Inventory/MealPlans.vue'),
      },
      {
        path: '/extras',
        name: 'extras',
        meta: {
          title: 'Extras',
          documentTitle: '*pages.extras.title',
          translationKey: ['extras'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "extras" */ '@/views/Inventory/Extras.vue'),
      },
      {
        path: '/promotions',
        name: 'promotions',
        meta: {
          title: 'Promotions',
          documentTitle: '*promotions.title',
          translationKey: ['promotions'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "promotions" */ '@/views/Inventory/PromotionsPage.vue'),
      },
      {
        path: '/contracts',
        name: 'contracts',
        meta: {
          title: 'Contracts',
          documentTitle: '*pages.contracts.title',
          translationKey: ['contracts'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "contracts" */ '@/views/Inventory/Contracts.vue'),
      },
      {
        path: '/room-types',
        name: 'roomtypes',
        meta: {
          title: 'Room types',
          documentTitle: '*pages.roomtypes.title',
          translationKey: ['roomTypes'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "room-types" */ '@/views/Inventory/RoomTypes.vue'),
      },
      {
        path: '/rate-plans',
        name: 'rateplans',
        meta: {
          title: 'Rate plans',
          documentTitle: '*pages.rateplans.title',
          translationKey: ['ratePlans'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "rate-plans" */ '@/views/Inventory/ProductsPage.vue'),
      },
      {
        path: '/master-data/:tab?',
        name: 'masterdata',
        meta: {
          title: 'Master Data',
          documentTitle: '*pages.masterdata.title',
          translationKey: ['masterData'],
          loadMeasurement: true,
          pageID: 220,
        },
        component: () => import(/* webpackChunkName: "master-data" */ '@/views/Property/MasterData.vue'),
      },
      {
        path: '/booking-status/:tab?',
        name: 'booking',
        meta: {
          title: 'Booking Status',
          documentTitle: '*pages.booking.title',
          translationKey: ['booking'],
          loadMeasurement: true,
          pageID: 221,
        },
        component: () => import(/* webpackChunkName: "booking" */ '@/views/Property/BookingStatus.vue'),
      },
      {
        path: '/contact-persons',
        name: 'contactpersons',
        meta: {
          title: 'Contact Persons',
          documentTitle: '*pages.contactpersons.title',
          translationKey: ['contactPersons'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "contact-persons" */ '@/views/Property/ContactPersons.vue'),
      },
      {
        path: '/nearby',
        name: 'nearby',
        meta: {
          title: 'What\'s nearby',
          documentTitle: '*pages.nearby.title',
          translationKey: ['nearby'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "nearby" */ '@/views/Property/NearBy.vue'),
      },
      {
        path: '/facilities',
        name: 'facilities',
        meta: {
          title: 'Facilities',
          documentTitle: '*pages.facilities.title',
          translationKey: ['facilities'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "facilities" */ '@/views/Property/Facilities.vue'),
      },
      {
        path: '/policies',
        name: 'policies',
        meta: {
          title: 'Policies',
          documentTitle: '*pages.policies.title',
          translationKey: ['policies'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "policies" */ '@/views/Inventory/Policies.vue'),
      },
      {
        path: '/settings',
        name: 'settings',
        meta: {
          title: 'Settings',
          documentTitle: '*pages.settings.title',
          translationKey: ['settings'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "settings" */ '@/views/Inventory/Settings.vue'),
      },
      {
        path: '/photos',
        name: 'photos',
        meta: {
          title: 'Photos',
          documentTitle: '*pages.photos.title',
          translationKey: ['photos'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "photos" */ '@/views/Property/Photos.vue'),
      },
      {
        path: '/file-administration',
        name: 'fileadministration',
        meta: {
          title: 'FileAdministration',
          documentTitle: '*pages.fileadministration.title',
          translationKey: ['fileadministration'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "file-administration" */ '@/views/Property/FileAdministration.vue'),
      },
      {
        path: '/onlinepresence',
        name: 'onlinepresence',
        meta: {
          title: 'OnlinePresence',
          documentTitle: '*pages.onlinepresence.title',
          translationKey: ['onlinepresence'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "online-presence" */ '@/views/Property/OnlinePresence.vue'),
      },
      {
        path: '/channels/:tab?',
        name: routerHelper.channelsRouteName,
        meta: {
          title: 'Channels',
          documentTitle: '*pages.channels.title',
          translationKey: ['channels'],
          loadMeasurement: true,
          pageID: 219,
        },
        component: () => import(/* webpackChunkName: "channels" */ '@/views/Connectivity/Channels.vue'),
      },
      {
        path: '/marketPlace',
        name: 'marketPlace',
        meta: {
          title: 'MarketPlace',
          documentTitle: '*pages.marketPlace.title',
          translationKey: ['marketPlace'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "marketPlace" */ '@/views/MarketPlace/MarketPlace.vue'),
      },
      {
        path: '/distributors/bookingengine/:id/:tab?',
        name: routerHelper.bookingEngineRouteName,
        meta: {
          permission: 'distributors',
          title: 'Distributors',
          documentTitle: '*pages.channels.title',
          translationKey: ['channel'],
          loadMeasurement: true,
          pageID: 235,
        },
        component: () => import(/* webpackChunkName: "booking-engine" */ '@/views/Connectivity/BookingEngine.vue'),
      },
      {
        path: '/distributors/:name/:id/:tab?',
        name: routerHelper.connectedChannelRouteName,
        meta: {
          permission: 'bookingengine',
          title: 'Channels',
          documentTitle: '*pages.channels.title',
          translationKey: ['channel'],
          loadMeasurement: true,
          pageID: 235,
        },
        component: () => import(/* webpackChunkName: "booking-engine" */ '@/views/Connectivity/BookingEngine.vue'),
      },
      {
        path: '/systems/:tab?',
        name: 'systems',
        meta: {
          title: 'Systems',
          documentTitle: '*pages.systems.title',
          translationKey: ['systems'],
          loadMeasurement: true,
          pageID: 229,
        },
        component: () => import(/* webpackChunkName: "systems" */ '@/views/Connectivity/Systems.vue'),
      },
      {
        path: '/properties',
        name: 'properties',
        meta: {
          title: 'Properties',
          documentTitle: '*pages.properties.title',
          empty: true,
          translationKey: ['properties'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "properties" */ '@/views/Management/PropertiesAndPropertiesGroup.vue'),
      },
      {
        path: '/partners',
        name: 'partners',
        meta: {
          title: 'Partners',
          documentTitle: '*pages.partners.title',
          empty: true,
          translationKey: ['partners'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "partners" */ '@/views/Management/Partners.vue'),
      },
      {
        path: '/generalsettings',
        name: 'generalsettings',
        meta: {
          title: 'General Settings',
          documentTitle: '*pages.generalsettings.title',
          empty: true,
          translationKey: ['generalsettings'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "general-settings" */ '@/views/Management/GeneralSettings.vue'),
      },
      {
        path: '/descriptions',
        name: 'description',
        meta: {
          title: 'Descriptions',
          documentTitle: '*pages.description.title',
          translationKey: ['description'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "descriptions" */ '@/views/Property/Descriptions.vue'),
      },
      {
        path: '/channelshealth',
        name: 'channelshealth',
        meta: {
          title: 'Channels Health',
          documentTitle: '*channelshealth.title',
          translationKey: ['channelshealth'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "channels-health" */ '@/views/SystemHealth/ChannelsHealth.vue'),
      },
      {
        path: '/performance',
        name: 'performance',
        meta: {
          title: 'Loading Performance Reports',
          documentTitle: '*loadingPerformanceReports.title',
          translationKey: 'adminLoadingPerformanceReports',
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "loading-performance-reports" */ '@/views/SystemHealth/PerformanceReports.vue'),
      },
      {
        path: '/performance-report',
        name: 'performanceReports',
        meta: {
          title: 'Performance Report',
          documentTitle: '*reports.performanceReportTitle',
          translationKey: 'adminLoadingPerformanceReports',
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "performance-reports" */ '@/views/Reports/PerformanceReports.vue'),
      },
      {
        path: '/error-reports',
        name: 'errorreport',
        meta: {
          title: 'Error Reports',
          documentTitle: '*errorReports.title',
          translationKey: 'errorReports',
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "error-reports" */ '@/views/SystemHealth/ErrorReports.vue'),
      },
      {
        path: '/activity',
        name: 'activityreport',
        meta: {
          title: 'User Activity',
          documentTitle: '*activityReports.title',
          translationKey: 'activity',
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "activity-reports" */ '@/views/SystemHealth/ActivityReports.vue'),
      },
      {
        path: '/webhooks',
        name: 'webhooks',
        meta: {
          title: 'Webhooks',
          documentTitle: '*pages.groups.title',
          empty: true,
          translationKey: ['webhooks'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "webhooks" */ '@/views/Management/Webhooks.vue'),
      },
      {
        path: '/property-group-settings',
        name: routerHelper.coloursmasterdataRouteName,
        meta: {
          title: 'Colors&Roles',
          documentTitle: '*navbarTitles.coloursmasterdata',
          empty: true,
          translationKey: ['webhooks'],
          loadMeasurement: true,
        },
        component: () => import(/* webpackChunkName: "webhooks" */ '@/views/Management/PropertyGroupSettings.vue'),
      },
      {
        path: '/no-perms',
        name: routerHelper.noPermsName,
        component: () => import(/* webpackChunkName: "no-perms" */ '@/views/User/NoPermissions.vue'),
        meta: {
          // centered: true,
          sideBar: false,
          empty: true,
          translationKey: ['noPerms'],
        },
      },
    ],
  },
];

if (routerHelper.redirectConstant) {
  if (routerHelper.redirectConstant.urlKey === routerHelper.eHotelSuiteUrlKey) {
    routes.push(eHotelSuiteRoutes);
  }
  if (routerHelper.redirectConstant.urlKey === routerHelper.mappingMasterUrlKey) {
    routes.push(mappingMasterRoutes);
  }
  if (routerHelper.redirectConstant.urlKey === routerHelper.hotelLinkageUrlKey) {
    routes.push(hotelLinkageRoutes);
  }
  if (routerHelper.redirectConstant.urlKey === routerHelper.testPropertyGroup) {
    routes.push(testAuthRoutes);
  }
} else {
  routes.push(authRoutes);
}

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  linkExactActiveClass: 'exact-active',
  linkActiveClass: 'active',
  routes,
});

export default router;

export { router };

const initialUserId = StorageService.getUserId();
const initialProperty = StorageService.getProperty();
let initialUserCheckDone = false;
let initialUserInfoCheckDone = false;

let $store = null;
const initStore = () => {
  Vue.nextTick(() => {
    // save $store for future use
    $store = router.app.$store;

    // Disable maintenance mode / set maintenance mode state to false
    // $store.commit('system/disableMaintenanceMode');
  });
};

const checkLoggedIn = () => new Promise((resolve) => {
  Vue.nextTick(async () => {
    await Promise.allSettled([
      $store.dispatch('user/getUser'),
    ]);
    resolve();
  });
});

const getInfo = () => new Promise((resolve) => {
  Vue.nextTick(async () => {
    await Promise.allSettled([
      $store.dispatch('user/getInfo'),
    ]);
    resolve();
  });
});

const updateMeta = (store, to) => {
  const {
    documentTitle,
    sideBar,
    centered,
    stretch,
  } = to.meta;
  store.commit('pageTitle', documentTitle);
  store.commit('sidebar', sideBar);
  store.commit('centered', centered);
  store.commit('stretch', stretch);
};

const initialPageByPerms = () => {
  const perms = $store.getters['user/allowedUserPages'];
  if (perms.includes('properties')) return 'properties';
  if (perms.includes('users')) return 'users';
  if (perms.includes('group')) return 'group';
  return routerHelper.noPermsName;
};

const initialPageForProperty = () => {
  const perms = $store.getters['user/allowedPages'];
  if (!perms.length) return routerHelper.noPermsName;
  return perms[0];
};

// const getInitialRoute = () => {
//   // const isAdmin = $store.getters['user/isAdmin'];
//   // if (isAdmin)
//   // const hasProperties = $store.getters['user/hasProperties'];
//   // return hasProperties ? initialPageForProperty() : initialPageByPerms();
//   return 'dashboard';
// };

router.afterEach(() => {
  setTimeout(() => {
    $store.commit('appLoading', false);
  }, 100);
});

router.beforeEach(async (to, from, next) => {
  // eslint-disable-next-line no-underscore-dangle
  const _next = next;

  // eslint-disable-next-line no-param-reassign
  next = function newnext(loc) {
    return _next(loc);
  };
  await initStore();

  ApiService.setPageId(to.name);

  if ($store.getters['system/maintenanceIsOn']) {
    if (to.path !== '/maintenance') {
      return next('/maintenance');
    }
    return next();
  }
  // refresh token
  // await getCSRFCookie();

  if (!initialUserCheckDone) {
    await getInfo();
    initialUserCheckDone = true;
  }

  if (!initialUserCheckDone) {
    await $store.dispatch('auth/logout', { forced: true, stay: true });
  }

  if (!initialUserInfoCheckDone && (initialUserId || initialProperty)) {
    await checkLoggedIn();
    initialUserInfoCheckDone = true;
  }

  // clear server-validation states in store modules
  $store.dispatch('clearErrors');

  // set current route name
  $store.commit('auth/currentRoute', to.name);

  // set current route name
  $store.commit('pageBreadcrumb', null);
  $store.commit('pageBreadcrumbSub', null);

  // By default, all routes are protected for users only
  const isGuestOnly = to.matched.filter((item) => item.parent)
    .some((r) => r.meta.guestOnly);
  const isPublic = to.matched.filter((item) => item.parent)
    .some((r) => r.meta.public);
  const isForAdmin = to.matched.filter((item) => item.parent)
    .some((r) => r.meta.admin === true);
  const forEmptyProperties = to.matched.filter((item) => item.parent)
    .some((r) => r.meta.empty === true);
  const disabledForAgent = to.matched.filter((item) => item.parent)
    .some((r) => r.meta.agent === false);

  const isLoggedIn = !!StorageService.getUser();
  // const isForMaintenanceMode = to.matched.some((r) => r.meta.maintenance === true);
  const currentProperty = $store.getters['user/currentProperty'];
  const perms = currentProperty?.landing_property?.perms ?? [];

  const hasProperties = $store.getters['user/hasProperties'];
  const isAdmin = $store.getters['user/isAdmin'];
  const isAgentDomain = $store.getters['user/isAgentDomain'];
  const isAgentUser = $store.getters['user/isAgentUser'];
  const maintenanceIsOn = $store.getters['system/maintenanceIsOn'];
  // check if user has permissions to access this page
  let isEmailVerified = false;
  try {
    isEmailVerified = isLoggedIn ? await UserService.isEmailVerified() : false;
  } catch (error) {
    /* eslint-disable no-console */
    console.log(`isEmailVerified: ${isEmailVerified}`);
    window.location.href = router.resolve({ name: routerHelper.loginRouteName }).href;
  }

  if (isLoggedIn && isEmailVerified && $store.getters['user/setupComplete']) {
    if (!perms.includes(to.name)) {
      if (!routerHelper.excludedPages.includes(to.name) && !isAdmin) {
        return next({ name: routerHelper.dashboardRouteName });
      }
    }

    // Checking the booking engine access
    if (to.name === routerHelper.bookingEngineRouteName || to.name === routerHelper.connectedChannelRouteName) {
      if (to.params.id === bookingEngineId
        && !perms.includes(routerHelper.bookingEngineRouteName)) {
        return next({ name: routerHelper.dashboardRouteName });
      }
      if (to.params.id !== bookingEngineId
        && !perms.includes(routerHelper.distributionRouteName)) {
        return next({ name: routerHelper.dashboardRouteName });
      }
    }
  }

  // prevent guests from visiting logged-in pages
  if (!isPublic && !isLoggedIn) {
    const redirect = to.fullPath;
    const toLogin = { name: routerHelper.loginRouteName };
    if (redirect !== '/') {
      toLogin.query = { redirect };
    }
    return next(toLogin);
  }

  if (isLoggedIn && maintenanceIsOn) {
    window.location.href = '/maintenance';
    // return next({ name: 'maintenance' });
  }

  if (to.name === 'home') {
    return next({ name: routerHelper.dashboardRouteName });
  }

  if (!isLoggedIn) {
    // prevent guests from visiting pages disabled for agent users
    if (isAgentDomain && disabledForAgent) {
      return next({ name: routerHelper.loginRouteName });
    }
    updateMeta($store, to);
    if (maintenanceIsOn) {
      return next({ name: 'maintenance' });
    }

    return next();
  }

  const initial = hasProperties ? initialPageForProperty() : initialPageByPerms();
  if (isLoggedIn && isAgentUser && (to.name === routerHelper.loginRouteName && to.params.uuid)) {
    return next();
  }

  // prevent logged-in users from visiting guests-only pages
  // or pages disabled for agent users
  if (isLoggedIn && (isGuestOnly || (isAgentUser && disabledForAgent))) {
    return next({ name: initial });
  }

  // check conditions that prevents users from continue
  // until they fill some information or make some actions
  // ---
  // initial check has been done already, we can use $store here
  // const { $store } = router.app;

  // check for verified email
  if (!isEmailVerified) {
    // eslint-disable-next-line max-len
    if (from.name === routerHelper.verificationNoticeRouteName) {
      // await UserService.logout();
      return next(false);
    }
    if (to.name !== routerHelper.verificationNoticeRouteName) {
      return next(
        {
          name: routerHelper.verificationNoticeRouteName,
          query: to.query,
          params: to.params,
        },
      );
    }
    updateMeta($store, to);
    return next();
  }
  if (to.name === routerHelper.verificationNoticeRouteName) {
    return next({ name: initial });
  }
  // check for filled information
  if (!$store.getters['user/requiredFilled']) {
    if (from.name === routerHelper.detailsRouteName) {
      return next(false);
    }
    if (to.name !== routerHelper.detailsRouteName) {
      return next({ name: routerHelper.detailsRouteName });
    }
    updateMeta($store, to);
    return next();
  }
  if (to.name === routerHelper.detailsRouteName) {
    return next({ name: initial });
  }

  // prevent logged-in users from visiting guests-only pages
  // or pages disabled for agent users
  if (isLoggedIn && (isGuestOnly || (isAgentUser && disabledForAgent))) {
    return next({ name: initial });
  }

  // check for setup
  if (!$store.getters['user/setupComplete']) {
    if (from.name === routerHelper.setupRouteName) {
      return next(false);
    }
    if (to.name !== routerHelper.setupRouteName) {
      return next({ name: routerHelper.setupRouteName });
    }
    updateMeta($store, to);
    return next();
  }

  if (to.name === routerHelper.setupRouteName) {
    return next({ name: initial });
  }

  // check for admin rights
  if (isForAdmin && !isAdmin) {
    return next({ name: initial });
  }

  // check for no properties
  if (!hasProperties && !forEmptyProperties) {
    return next({ name: initial });
  }

  updateMeta($store, to);

  return next();
});
